/**
 * NIC-460: Keep cart Promo line-item properties in sync with live promo metafields.
 * - Layer A (Liquid) shows/hides promo from live metafields in cart UI.
 * - Layer B clears/restores `Promo` properties via Cart API without a second full drawer redraw.
 */
(function () {
  const SYNC_ATTR = 'data-cart-promo-syncing';
  const CHECKOUT_RESUME_ATTR = 'data-cart-promo-checkout-resume';

  function getCartBaseUrl() {
    const cartUrl = (window.theme && theme.routes && theme.routes.cart) || window.Shopify?.routes?.root + 'cart' || '/cart';
    return String(cartUrl).replace(/\.js$/, '').replace(/\/$/, '');
  }

  function getCartJsUrl() {
    return `${getCartBaseUrl()}.js`;
  }

  function getCartChangeUrl() {
    return `${getCartBaseUrl()}/change.js`;
  }

  function findCartForm(scope) {
    if (!scope) return document.querySelector('cart-drawer cart-form, main-cart cart-form, cart-form');
    if (scope.matches && scope.matches('cart-form')) return scope;
    if (scope.querySelector) {
      return scope.querySelector('cart-form') || scope.closest?.('cart-form') || findCartForm(null);
    }
    return findCartForm(null);
  }

  function refreshCartForm(cartForm) {
    return new Promise((resolve) => {
      if (!cartForm || typeof cartForm.refresh !== 'function') {
        resolve(false);
        return;
      }

      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        cartForm.removeEventListener('on:cart:after-merge', onMerge);
        resolve(true);
      };

      const onMerge = () => finish();
      cartForm.addEventListener('on:cart:after-merge', onMerge);
      cartForm.refresh();
      setTimeout(finish, 8000);
    });
  }

  async function fetchCart() {
    const response = await fetch(getCartJsUrl(), {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) {
      throw new Error(`Cart fetch failed (${response.status})`);
    }
    return response.json();
  }

  async function updatePromoProperty(cartItem, promoMessage) {
    const nextProperties = Object.assign({}, cartItem.properties || {});
    const nextValue = promoMessage == null ? '' : String(promoMessage);

    if (!nextValue) {
      if (!Object.prototype.hasOwnProperty.call(nextProperties, 'Promo') || !nextProperties.Promo) {
        return null;
      }
      nextProperties.Promo = '';
    } else if (nextProperties.Promo === nextValue) {
      return null;
    } else {
      nextProperties.Promo = nextValue;
    }

    const response = await fetch(getCartChangeUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        id: cartItem.key,
        quantity: cartItem.quantity,
        properties: nextProperties,
      }),
    });

    if (!response.ok) {
      throw new Error(`Cart change failed (${response.status})`);
    }

    return response.json();
  }

  function findCartItemForEl(cart, el) {
    const lineKey = el.getAttribute('data-line-key');
    if (lineKey) {
      const byKey = (cart.items || []).find((item) => item.key === lineKey);
      if (byKey) return byKey;
    }

    const variantId = el.getAttribute('data-variant-id');
    if (!variantId) return null;

    return (cart.items || []).find((item) => String(item.variant_id) === String(variantId));
  }

  function findUpdatedLineItem(cart, previousItem, variantId) {
    if (!cart || !cart.items) return null;

    if (previousItem && previousItem.key) {
      const sameKey = cart.items.find((item) => item.key === previousItem.key);
      if (sameKey) return sameKey;
    }

    const matches = cart.items.filter((item) => String(item.variant_id) === String(variantId));
    if (matches.length === 1) return matches[0];

    if (previousItem) {
      return (
        matches.find((item) => item.quantity === previousItem.quantity) ||
        matches[0] ||
        null
      );
    }

    return matches[0] || null;
  }

  /**
   * Keep quantity controls working after property updates change the line key.
   * Avoids a second full section refresh (UX flash).
   */
  function patchCartItemAfterPromoSync(el, updatedItem, mode) {
    if (!el || !updatedItem) return;

    if (updatedItem.key) {
      el.setAttribute('data-line-key', updatedItem.key);
      const quantityInput = el.querySelector('.cart-item__quantity-input');
      if (quantityInput) {
        quantityInput.setAttribute('data-key', updatedItem.key);
        quantityInput.dataset.key = updatedItem.key;
      }

      el.querySelectorAll('a[href*="cart/change"]').forEach((link) => {
        try {
          const url = new URL(link.href, window.location.origin);
          url.searchParams.set('id', updatedItem.key);
          link.href = url.pathname + url.search;
        } catch (e) {
          // Ignore malformed hrefs; quantity AJAX path still uses data-key.
        }
      });
    }

    el.removeAttribute('data-promo-stale');
    el.removeAttribute('data-promo-missing');
    el.removeAttribute('data-active-promo-message');

    if (mode === 'restore' && updatedItem.properties && updatedItem.properties.Promo) {
      el.setAttribute('data-has-promo-property', 'true');
    } else if (mode === 'clear') {
      el.removeAttribute('data-has-promo-property');
    }
  }

  async function syncPromoProperties(scope) {
    const root = scope || document;
    const staleItems = Array.from(root.querySelectorAll('.cart-item[data-promo-stale="true"]'));
    const missingItems = Array.from(root.querySelectorAll('.cart-item[data-promo-missing="true"]'));

    if (!staleItems.length && !missingItems.length) return false;

    let cart = await fetchCart();
    let changed = false;

    for (const el of staleItems) {
      const cartItem = findCartItemForEl(cart, el);
      if (!cartItem || !cartItem.properties || !cartItem.properties.Promo) continue;
      const updatedCart = await updatePromoProperty(cartItem, '');
      if (!updatedCart) continue;
      cart = updatedCart;
      changed = true;
      const updatedItem = findUpdatedLineItem(cart, cartItem, el.getAttribute('data-variant-id'));
      patchCartItemAfterPromoSync(el, updatedItem || cartItem, 'clear');
    }

    for (const el of missingItems) {
      const promoMessage = (el.getAttribute('data-active-promo-message') || '').trim();
      if (!promoMessage) continue;

      const cartItem = findCartItemForEl(cart, el);
      if (!cartItem) continue;

      const updatedCart = await updatePromoProperty(cartItem, promoMessage);
      if (!updatedCart) continue;
      cart = updatedCart;
      changed = true;
      const updatedItem = findUpdatedLineItem(cart, cartItem, el.getAttribute('data-variant-id'));
      patchCartItemAfterPromoSync(el, updatedItem || cartItem, 'restore');
    }

    // Do not dispatch on:cart:change here — that re-renders the whole drawer and causes a UX flash.
    // Liquid already rendered the correct promo visibility on the prior refresh.
    return changed;
  }

  async function syncCartPromoMessages(options = {}) {
    const { scope = document, refreshFirst = false } = options;

    if (document.documentElement.hasAttribute(SYNC_ATTR)) {
      return false;
    }

    const cartForm = findCartForm(scope);
    const workingScope = cartForm || scope;

    document.documentElement.setAttribute(SYNC_ATTR, 'true');

    try {
      if (refreshFirst && cartForm) {
        await refreshCartForm(cartForm);
      }
      return await syncPromoProperties(cartForm || workingScope);
    } catch (error) {
      // Fail closed for display (Liquid Layer A still drives UI); do not break cart UX.
      console.warn('[cart-promo-sync]', error && error.message ? error.message : error);
      return false;
    } finally {
      document.documentElement.removeAttribute(SYNC_ATTR);
    }
  }

  function isCheckoutTrigger(el) {
    if (!el) return false;
    if (el.matches?.('#cartform [name="checkout"], [name="checkout"]')) return true;
    if (el.matches?.('.checkout-buttons a[href*="/checkout"], a[href*="/checkout"]')) {
      return !!el.closest('.checkout-buttons, cart-drawer, main-cart, #cartform');
    }
    if (el.matches?.('.additional-checkout-buttons input, .additional-checkout-buttons button, .additional-checkout-buttons a')) {
      return true;
    }
    return false;
  }

  function resumeCheckout(trigger) {
    if (!trigger) return;

    trigger.setAttribute(CHECKOUT_RESUME_ATTR, 'true');

    try {
      if (trigger.tagName === 'A' && trigger.href) {
        window.location.href = trigger.href;
        return;
      }

      if (trigger.type === 'submit' || trigger.name === 'checkout') {
        const form = trigger.form || trigger.closest('form');
        if (form) {
          if (typeof form.requestSubmit === 'function') {
            form.requestSubmit(trigger);
          } else {
            form.submit();
          }
          return;
        }
      }

      trigger.click();
    } finally {
      setTimeout(() => trigger.removeAttribute(CHECKOUT_RESUME_ATTR), 0);
    }
  }

  function bindCheckoutGuard() {
    document.addEventListener(
      'click',
      async (evt) => {
        const trigger = evt.target.closest?.(
          '#cartform [name="checkout"], .checkout-buttons a[href*="/checkout"], .additional-checkout-buttons input, .additional-checkout-buttons button, .additional-checkout-buttons a'
        );
        if (!trigger || !isCheckoutTrigger(trigger)) return;
        if (trigger.hasAttribute(CHECKOUT_RESUME_ATTR)) return;

        const scope =
          trigger.closest('cart-form') ||
          document.querySelector('cart-drawer cart-form, main-cart cart-form, cart-form');
        if (!scope) return;

        const needsSync = scope.querySelector(
          '.cart-item[data-promo-stale="true"], .cart-item[data-promo-missing="true"]'
        );
        if (!needsSync) return;

        evt.preventDefault();
        evt.stopPropagation();

        await syncCartPromoMessages({
          scope,
          refreshFirst: false,
        });

        resumeCheckout(trigger);
      },
      true
    );
  }

  window.theme = window.theme || {};
  theme.syncCartPromoMessages = syncCartPromoMessages;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindCheckoutGuard, { once: true });
  } else {
    bindCheckoutGuard();
  }
})();
